// SPDX-License-Identifier: MIT
// Export Control Middleware - gates kepler export actions when secure datasets present (Phase 6)

import type { Middleware } from 'redux';
import { selectHasSecureDatasets } from '../selectors/exportControl';
import { toggleModal, toggleSidePanel, addNotification } from '@kepler.gl/actions';
import type { RootState } from '../store';
import {
  deleteAllFeatureAnnotations,
  FEATURE_ANNOTATIONS_ACTIONS,
} from '../integrations/feature-annotations/feature-annotations-slice';

// Kepler modal ids vary by version; rather than rely on exact ids, block any modal id containing 'export'
// Historical ids include: 'exportImage', 'exportData', 'export-map', 'exportData', etc.
const LEGACY_EXPORT_IDS = ['exportData', 'exportMap'];
const isBlockedExportModal = (id: any) => typeof id === 'string' && /export/i.test(id);

// Data table / dataset side panel identifiers (kepler versions differ: 'data' vs 'dataset').
const DATA_PANEL_IDS = ['data', 'dataset'];
const isDataPanelId = (id: any) => typeof id === 'string' && DATA_PANEL_IDS.includes(id);

// Action type regexes to detect attempts to open data table via kepler actions
const DATA_TABLE_ACTION_PATTERNS = [
  /SHOW_DATASET_TABLE/i, // legacy explicit table open
  /TOGGLE_SIDE_PANEL/i, // payload inspected for 'data' | 'dataset'
];

let prevHasSecure = false; // module local – sufficient for single store instance

export const exportControlMiddleware: Middleware<unknown, RootState> = store => next => action => {
  // Pre-block: intercept attempts to open blocked modals or data table while secure datasets present
  try {
    const a: any = action as any;
    if (a && a.type && typeof a.type === 'string') {
      if (a.type.includes('TOGGLE_MODAL')) {
        // generic match for @@kepler.gl/TOGGLE_MODAL
        const target = a.payload; // modal id requested
        const state = store.getState();
        const hasSecure = selectHasSecureDatasets(state as any);
        if (hasSecure && (LEGACY_EXPORT_IDS.includes(target) || isBlockedExportModal(target))) {
          // Notify & short‑circuit
          store.dispatch(
            addNotification({
              id: 'secure-export-blocked',
              type: 'warning',
              message: 'Secure mode: export blocked while secure datasets are loaded.',
            }) as any
          );
          return; // swallow action
        }
      }

      // Data table gating: block dataset table / side panel opening
      if (DATA_TABLE_ACTION_PATTERNS.some(r => r.test(a.type))) {
        const state = store.getState();
        const hasSecure = selectHasSecureDatasets(state as any);
        const payload = a.payload;
        const intendsOpenDataPanel = a.type.includes('TOGGLE_SIDE_PANEL') && isDataPanelId(payload);
        const intendsShowDatasetTable = /SHOW_DATASET_TABLE/i.test(a.type);
        if (hasSecure && (intendsOpenDataPanel || intendsShowDatasetTable)) {
          store.dispatch(
            addNotification({
              id: 'secure-datatable-blocked',
              type: 'warning',
              message: 'Secure mode: data table blocked while secure datasets are loaded.',
            }) as any
          );
          return; // swallow action
        }
      }

      // Export gate: block EXPORT_DRAWN_FEATURES when secure datasets are present.
      // This is the middleware enforcement point; the AnnotationsPanelTab also gates
      // the button in the UI (belt-and-suspenders).
      if (a.type === FEATURE_ANNOTATIONS_ACTIONS.EXPORT_DRAWN_FEATURES) {
        const state = store.getState();
        const hasSecure = selectHasSecureDatasets(state as any);
        if (hasSecure) {
          store.dispatch(
            addNotification({
              id: 'export-drawn-blocked',
              type: 'warning',
              message: 'Secure mode: export blocked while secure datasets are loaded.',
            }) as any
          );
          return; // swallow action
        }
      }

      // Annotation cleanup: when Kepler deletes a drawn feature, remove its
      // annotation data from the feature-annotations slice so state stays consistent.
      if (a.type === '@@kepler.gl/DELETE_FEATURE') {
        const featureId = a.payload?.id;
        if (featureId != null) {
          store.dispatch(deleteAllFeatureAnnotations(String(featureId)) as any);
        }
      }
    }
  } catch {
    /* ignore pre-block errors */
  }

  const result = next(action);

  // Post processing: auto-close if state transitioned to secure while restricted UI open; re-enable notice.
  try {
    const state = store.getState();
    const hasSecure = selectHasSecureDatasets(state as any);
    const uiState = (state as any).keplerGl?.map?.uiState;
    const currentModal = uiState?.currentModal;
    const activeSidePanel = uiState?.activeSidePanel;
    // Some versions also surface visState.showDatasetTable; we defensively check it.
    const visState = (state as any).keplerGl?.map?.visState;
    const showDatasetTable = visState?.showDatasetTable;

    if (
      hasSecure &&
      (LEGACY_EXPORT_IDS.includes(currentModal) || isBlockedExportModal(currentModal))
    ) {
      store.dispatch(toggleModal(null) as any);
      store.dispatch(
        addNotification({
          id: 'secure-export-autoclose',
          type: 'info',
          message: 'Export dialog closed: secure mode active.',
        }) as any
      );
    }

    // Auto-close / move away from data panel or open dataset table when secure
    if (hasSecure && (isDataPanelId(activeSidePanel) || showDatasetTable)) {
      // Switch to a safe default panel (layer) instead of dataset/data
      store.dispatch(toggleSidePanel('layer') as any);
      store.dispatch(
        addNotification({
          id: 'secure-datatable-autoclose',
          type: 'info',
          message: 'Data table closed: secure mode active.',
        }) as any
      );
    }

    if (!hasSecure && prevHasSecure) {
      store.dispatch(
        addNotification({
          id: 'secure-export-cleared',
          type: 'success',
          message: 'Secure mode cleared: export re-enabled.',
        }) as any
      );
      store.dispatch(
        addNotification({
          id: 'secure-datatable-cleared',
          type: 'success',
          message: 'Secure mode cleared: data table re-enabled.',
        }) as any
      );
    }

    prevHasSecure = hasSecure;
  } catch {
    /* swallow */
  }

  return result;
};
